import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import styled from 'styled-components';

import { Link } from '@sorare/routing';
import { SeasonEligibility } from '@sorare/core/src/__generated__/globalTypes';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import {
  LinkBox,
  LinkOther,
  LinkOverlay,
} from '@sorare/core/src/atoms/navigation/Box';
import { BrandS, LabelM } from '@sorare/core/src/atoms/typography';
import { TokenMetas } from '@sorare/core/src/components/token/TokenMetas';
import { ANY_SPORT_PLAYERS_SLUG } from '@sorare/core/src/constants/__generated__/routes';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';
import { ClickMarketHomeSection_Target } from '@sorare/core/src/protos/events/so5/shared/events';

import { ItemOwnerSince } from '#src/components/ItemPreview/ItemOwnerSince/index.tsx';
import { OwnerMyClubLink } from '#src/components/TokenPreview/TokenOwner/index.tsx';
import { FlexCard } from '#src/components/token/FlexCard/index.tsx';
import { TokenOwnerPrice } from '#src/components/token/TokenOwnerPrice/index.tsx';
import { TokenTransferTypeIcon } from '#src/components/token/TokenTransferTypeIcon/index.tsx';

import type { RecentPurchase_tokenOwner } from './__generated__/index.graphql.ts';

const Root = styled(Vertical)`
  min-width: 300px;
`;

const BorderLinkBox = styled(Horizontal).attrs({ as: LinkBox })`
  border: 1px solid var(--c-nd-200);
  border-radius: var(--unit);
  padding: var(--intermediate-unit);
  height: 100%;
`;

const CardWrapper = styled.div`
  width: 66px;
`;

const StyledBrandS = styled(BrandS)`
  white-space: nowrap;
  svg,
  img {
    margin-right: var(--unit);
  }
`;

const StyledLabelM = styled(LabelM).attrs({
  as: 'span',
})`
  white-space: nowrap;
`;

type Props = {
  tokenOwner: RecentPurchase_tokenOwner;
  onMarketClick: (slug: string, seasonality: SeasonEligibility) => void;
  track?: (p: {
    target: ClickMarketHomeSection_Target;
    playerSlug?: string;
    cardSlug?: string;
    offerId?: string;
    ownerSlug?: string;
  }) => void;
};
export const RecentPurchase = ({ tokenOwner, onMarketClick, track }: Props) => {
  const trackFn = (target: ClickMarketHomeSection_Target) =>
    track?.({
      target,
      playerSlug: tokenOwner.card.anyPlayer.slug,
      cardSlug: tokenOwner.card.slug,
      ...(tokenOwner?.deal &&
        'id' in tokenOwner.deal && { offerId: tokenOwner.deal.id }),
      ownerSlug: tokenOwner.user?.slug,
    });
  return (
    <Root>
      <BorderLinkBox>
        <CardWrapper>
          <LinkOverlay
            as="button"
            onClick={() => {
              trackFn(ClickMarketHomeSection_Target.PLAYER_MARKET_DRAWER);
              onMarketClick(
                tokenOwner.card.anyPlayer.slug,
                tokenOwner.card.inSeasonEligible
                  ? SeasonEligibility.IN_SEASON
                  : SeasonEligibility.CLASSIC
              );
            }}
          >
            <FlexCard card={tokenOwner.card} width={80} />
          </LinkOverlay>
        </CardWrapper>
        <Vertical gap={0.5}>
          <StyledBrandS>
            <TokenTransferTypeIcon transferType={tokenOwner.transferType} />
            <LinkOther as="span">
              <Link
                to={generateSportPath(ANY_SPORT_PLAYERS_SLUG, {
                  params: {
                    slug: tokenOwner.card.anyPlayer.slug,
                  },
                  sport: tokenOwner.card.sport,
                })}
                onClick={() =>
                  trackFn(ClickMarketHomeSection_Target.PLAYER_PAGE)
                }
              >
                {tokenOwner.card.anyPlayer.displayName}
              </Link>
            </LinkOther>
          </StyledBrandS>
          <StyledLabelM color="var(--c-nd-600)">
            <TokenMetas card={tokenOwner.card} separator=" - " />
          </StyledLabelM>
          <TokenOwnerPrice tokenOwner={tokenOwner} />
        </Vertical>
      </BorderLinkBox>
      <SBHorizontal>
        {!!tokenOwner.user && (
          <OwnerMyClubLink
            sport={tokenOwner.card.sport}
            withAvatar
            user={tokenOwner.user}
            onClick={() => trackFn(ClickMarketHomeSection_Target.USER_GALLERY)}
          />
        )}
        <ItemOwnerSince card={tokenOwner.card} />
      </SBHorizontal>
    </Root>
  );
};

RecentPurchase.fragments = {
  tokenOwner: gql`
    fragment RecentPurchase_tokenOwner on TokenOwner {
      id
      transferType
      deal {
        ... on TokenAuction {
          id
        }
        ... on TokenOffer {
          id
        }
        ... on TokenPrimaryOffer {
          id
        }
      }
      card {
        slug
        sport
        inSeasonEligible
        anyPlayer {
          slug
          displayName
        }
        ...FlexCard_anyCard
        ...ItemOwnerSince_anyCard
        ...TokenMetas_anyCard
      }
      user {
        slug
        ...OwnerMyClubLink_User
      }
      ...TokenOwnerPrice_tokenOwner
    }
    ${FlexCard.fragments.anyCard}
    ${ItemOwnerSince.fragments.anyCard}
    ${TokenMetas.fragments.anyCard}
    ${OwnerMyClubLink.fragments.user}
    ${TokenOwnerPrice.fragments.tokenOwner}
  ` as TypedDocumentNode<RecentPurchase_tokenOwner>,
};
